/**
 * Updates navigation menu item classes based on the current page URL.
 * This is necessary for single-page applications using Barba.js, as server-side
 * class generation only happens on the initial load. It ensures that classes like
 * `.current-menu-item` and `.current-menu-ancestor` are correctly applied after
 * a page transition.
 *
 * @param {string} currentUrl - The full URL of the current page.
 * @param {string[]} [menuSelectors=['.header__navigation', '.footer-navigation']] - An array of CSS selectors for the navigation menus to update.
 * @param {boolean} [debug=false] - Whether to log debug messages.
 */
export function updateNavMenuClasses(currentUrl, menuSelectors = ['.header__navigation', '.footer-navigation'], debug = false) {
    const navMenus = document.querySelectorAll(menuSelectors.join(', '));

    if (!navMenus.length) {
        return;
    }

    try {
        const url = new URL(currentUrl);
        const currentPath = (url.pathname === '/' ? '/' : url.pathname.replace(/\/$/, "")).toLowerCase();

        navMenus.forEach(nav => {
            const menuLinks = nav.querySelectorAll('li.menu-item a');
            let bestMatches = [];
            let longestMatchLength = -1;

            nav.querySelectorAll('li.menu-item').forEach(li => {
                li.classList.remove(
                    'current-menu-item',
                    'current_page_item',
                    'current-menu-ancestor',
                    'current-page-ancestor'
                );
            });

            menuLinks.forEach(link => {
                if (!link.href || link.getAttribute('href').startsWith('#')) return;

                try {
                    const linkUrl = new URL(link.href);
                    if (linkUrl.hostname !== url.hostname) return;

                    const linkPath = (linkUrl.pathname === '/' ? '/' : linkUrl.pathname.replace(/\/$/, "")).toLowerCase();

                    if (currentPath.startsWith(linkPath)) {
                        if (linkPath.length > longestMatchLength) {
                            longestMatchLength = linkPath.length;
                            bestMatches = [link];
                        } else if (linkPath.length === longestMatchLength) {
                            bestMatches.push(link);
                        }
                    }
                } catch (e) {
                    if (debug) console.warn('[Nav Updater] Invalid URL found in menu link:', link.href);
                }
            });

            if (bestMatches.length > 0) {
                bestMatches.forEach(bestMatch => {
                    const bestMatchLi = bestMatch.closest('li.menu-item');
                    if (!bestMatchLi) return;

                    const bestMatchUrl = new URL(bestMatch.href);
                    const bestMatchPath = bestMatchUrl.pathname;
                    const normalizedBestMatchPath = (bestMatchPath === '/' ? '/' : bestMatchPath.replace(/\/$/, "")).toLowerCase();

                    let parentLi = bestMatchLi;
                    while (parentLi) {
                        parentLi.classList.add('current-menu-ancestor', 'current-page-ancestor');
                        parentLi = parentLi.parentElement.closest('li.menu-item');
                    }

                    if (normalizedBestMatchPath === currentPath) {
                        bestMatchLi.classList.add('current-menu-item', 'current_page_item');
                    }
                });
            }
        });
    } catch (e) {
        if (debug) console.error('[Nav Updater] Could not parse current URL:', currentUrl, e);
    }
}